import { Button } from "../components/Button";
import { noHrefLink, SparkleContext, } from "../context";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
export const CARD_VARIANTS = [
    "primary",
    "active",
    "secondary",
    "tertiary",
    "highlight",
    "warning",
];
export const CARD_SIZES = ["xs", "sm", "md", "lg"];
const interactiveClasses = cn("cursor-pointer", "transition duration-200", "hover:bg-primary-100", "active:bg-primary-150", "disabled:text-primary-muted", "disabled:border-border", "disabled:pointer-events-none", 
// Keyboard focus ring. An `outline` (not a box-shadow ring) is used because the
// Card root is `overflow-hidden`, which would clip a ring but leaves an offset
// outline intact. `focus:outline-hidden` drops the always-on default so a mouse
// click stays clean; `focus-visible:` shows the ring on keyboard focus only.
"focus:outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-highlight-400");
const cardVariants = cva(cn("flex text-left group", "border border-border overflow-hidden", "text-foreground"), {
    variants: {
        variant: {
            primary: cn("bg-muted-background", "border-transparent"),
            active: cn("bg-muted-background", "border-border"),
            highlight: cn("bg-highlight-50", "border-transparent"),
            warning: cn("bg-warning-50", "border-transparent"),
            secondary: cn("bg-background", "border-border"),
            tertiary: cn("bg-background", "border-transparent"),
        },
        size: {
            xs: "px-2 py-1.5 rounded-lg",
            sm: "p-3 rounded-xl",
            md: "p-4 rounded-2xl",
            lg: "p-5 rounded-3xl",
        },
        selected: {
            true: cn("border-highlight-300", "ring-2 ring-highlight-200/70", "shadow-sm"),
            false: "",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "md",
        selected: false,
    },
});
const InnerCard = React.forwardRef(({ children, variant, size, className, onClick, href, target = "_blank", rel = "", replace, shallow, selected, isPulsing, style, onKeyDown, tabIndex, ...props }, ref) => {
    const { components } = React.useContext(SparkleContext);
    const Link = href ? components.link : noHrefLink;
    // Determine if the card is interactive based on href or onClick
    const isInteractive = Boolean(href || onClick);
    // A `<div role="button">` has no native keyboard activation, so mirror a
    // button: Enter and Space fire onClick. Space is prevented from scrolling
    // (and Enter from any default) before synthesizing a real click — going
    // through `.click()` keeps onClick's MouseEvent contract intact and composes
    // with any caller-supplied onKeyDown.
    const handleInteractiveKeyDown = (event) => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
        if (!event.defaultPrevented &&
            (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            event.currentTarget.click();
        }
    };
    const isSelected = Boolean(selected);
    const hasSelectionProp = typeof selected !== "undefined";
    const cardButtonClassNames = cn(cardVariants({ variant, size, selected: isSelected }), 
    // Apply interactive styles when either href or onClick is present
    isInteractive ? interactiveClasses : "", isPulsing && "animate-ring-pulse overflow-visible", className);
    const cardStyle = isPulsing ? { animationDuration: "3s", ...style } : style;
    if (href) {
        const linkContent = (React.createElement(Link, { href: href, className: isPulsing ? "block h-full w-full" : cardButtonClassNames, replace: replace, shallow: shallow, target: target, rel: rel, "aria-selected": hasSelectionProp ? isSelected : undefined }, children));
        if (isPulsing) {
            return (React.createElement("div", { className: cardButtonClassNames, style: cardStyle }, linkContent));
        }
        return linkContent;
    }
    return (React.createElement("div", { ref: ref, className: cardButtonClassNames, style: cardStyle, onClick: onClick, role: isInteractive ? "button" : undefined, tabIndex: tabIndex !== null && tabIndex !== void 0 ? tabIndex : (isInteractive ? 0 : undefined), onKeyDown: isInteractive ? handleInteractiveKeyDown : onKeyDown, "aria-pressed": isInteractive && hasSelectionProp ? isSelected : undefined, "aria-selected": hasSelectionProp ? isSelected : undefined, ...props }, children));
});
InnerCard.displayName = "InnerCard";
export const Card = React.forwardRef(({ containerClassName, className, action, ...props }, ref) => {
    return (React.createElement("div", { className: cn("group/card relative", containerClassName), ref: ref },
        React.createElement(InnerCard, { className: cn("h-full w-full", className), ...props }),
        action && React.createElement(CardActions, null, action)));
});
Card.displayName = "Card";
const CardActions = React.forwardRef(({ children, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("absolute right-2 top-2 transition-opacity sm:opacity-0", "group-focus-within/card:opacity-100 group-hover/card:opacity-100"), ...props }, children));
});
CardActions.displayName = "CardActions";
export const CardActionButton = React.forwardRef(({ className, variant = "outline", icon = XClose, ...props }, ref) => {
    return (React.createElement(Button, { ref: ref, variant: variant, icon: icon, className: className, ...props }));
});
CardActionButton.displayName = "CardActionButton";
const uncappedGridClasses = cn("@xxs:grid-cols-2", "@sm:grid-cols-3", "@lg:grid-cols-4", "@xl:grid-cols-5");
const adaptiveGridClasses = cn("@xxs:has-[>:nth-child(2)]:grid-cols-2", "@sm:has-[>:nth-child(3)]:grid-cols-3", "@lg:has-[>:nth-child(4)]:grid-cols-4", "@xl:has-[>:nth-child(5)]:grid-cols-5");
export const CardGrid = React.forwardRef(({ children, className, gridClassName, adaptColumns = false, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("@container", className), ...props },
        React.createElement("div", { className: cn("grid grid-cols-1 gap-2", gridClassName !== null && gridClassName !== void 0 ? gridClassName : (adaptColumns ? adaptiveGridClasses : uncappedGridClasses)) }, children)));
});
CardGrid.displayName = "CardGrid";
//# sourceMappingURL=Card.js.map